import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import '../dashboard/dashboard.css';
import './admin-requests.css';

interface PlanRequest {
  id: string;
  plan_id: string;
  status: 'new' | 'contacted' | 'active' | 'declined';
  message: string;
  created_at: string;
  updated_at: string;
  user_email: string;
  user_name: string;
  site_id: string | null;
  site_title: string | null;
  site_slug: string | null;
}

const statusLabels: Record<PlanRequest['status'], string> = {
  new: 'New',
  contacted: 'Contacted',
  active: 'Active',
  declined: 'Declined',
};

const statusBadgeClass: Record<PlanRequest['status'], string> = {
  new: 'sarnia-badge--info',
  contacted: 'sarnia-badge--warning',
  active: 'sarnia-badge--success',
  declined: 'sarnia-badge--neutral',
};

export function AdminRequestsPage() {
  const [requests, setRequests] = useState<PlanRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .getPlanRequests()
      .then((data) => {
        if (active) setRequests(data.requests);
      })
      .catch((err: Error) => {
        if (active) setError(err.message || 'Could not load plan requests.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  async function handleStatusChange(id: string, newStatus: PlanRequest['status']) {
    const previous = requests.find((request) => request.id === id)?.status;
    if (!previous || previous === newStatus) return;

    setError(null);
    setRequests((current) =>
      current.map((request) => (request.id === id ? { ...request, status: newStatus } : request)),
    );

    try {
      await api.updatePlanRequest(id, newStatus);
    } catch (err) {
      setError((err as Error).message || 'Could not update request status.');
      setRequests((current) =>
        current.map((request) => (request.id === id ? { ...request, status: previous } : request)),
      );
    }
  }

  return (
    <div className="admin-requests">
      <h1>Plan requests</h1>

      {loading && <p className="dash-page-subtitle">Loading plan requests…</p>}
      {error && (
        <div className="sarnia-card" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Plan</th>
              <th>Site</th>
              <th>Message</th>
              <th>Created</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => {
              const shortMessage = request.message
                ? request.message.length > 120
                  ? `${request.message.slice(0, 120)}…`
                  : request.message
                : '—';
              return (
                <tr key={request.id}>
                  <td>
                    <div>{request.user_name || '—'}</div>
                    <div className="apr-muted">{request.user_email}</div>
                  </td>
                  <td>{request.plan_id}</td>
                  <td>
                    {request.site_title ? (
                      <>
                        {request.site_title}
                        {request.site_slug && <div className="apr-muted">{request.site_slug}</div>}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td title={request.message || undefined}>{shortMessage}</td>
                  <td>{new Date(request.created_at).toLocaleDateString()}</td>
                  <td>
                    <span className={`sarnia-badge ${statusBadgeClass[request.status]}`}>
                      {statusLabels[request.status]}
                    </span>
                    <select
                      className="apr-status-select"
                      aria-label={`Status for ${request.user_name || request.user_email}'s request`}
                      value={request.status}
                      onChange={(event) => handleStatusChange(request.id, event.target.value as PlanRequest['status'])}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="active">Active</option>
                      <option value="declined">Declined</option>
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {!loading && !error && requests.length === 0 && (
        <p className="dash-page-subtitle">No plan requests yet.</p>
      )}
    </div>
  );
}
